import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, filter, map, observeOn } from 'rxjs/operators';
import {
  ClearActiveEmployee,
  LoadedListEmployeesFromLocalDBFailure,
  LoadedListEmployeesFromLocalDBSuccess,
  LoadListEmployeesFromLocalDB,
  SelectActiveEmployee,
  UpdatedListEmployeesSuccess,
  UpdateListEmployees,
} from './employees.actions';
import { EmployeeState } from './employees.reducer';
import { selectActiveEmployee } from './employees.selectors';
import { EmployeesProvider } from '@core/services/resources/employees-db-entity.provider';
import { Employee } from '@shared/classes/employee.class';
import { asyncScheduler, of } from 'rxjs';
import { LogService } from '@core/services/system/logger';
import { ILogger } from '@paymash/logging';
import { LocalStorage } from '@core/services/utils/localstorage.utils';
import { StorageKeys } from '@shared/constants';
import { EmployeeLogout } from '@state/router';

@Injectable()
export class EmployeesPageEffects {
  private readonly logger: ILogger = this.logService.createLogger('EmployeesPageStateEffects');

  constructor(
    private actions$: Actions,
    private logService: LogService,
    private localStorage: LocalStorage,
    private employeesDBProvider: EmployeesProvider,
    private store: Store<EmployeeState>,
    ) {}

  reSelectEmployee$ = createEffect(() => {
    return this.store.select(selectActiveEmployee).pipe(
      filter((employee: Employee) => !employee && this.isNeedReSelectEmployee()),
      map(() => EmployeeLogout())
    )
  });

  loadListEmployees$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LoadListEmployeesFromLocalDB),
      exhaustMap(() => this.employeesDBProvider.getList()
        .pipe(
          observeOn(asyncScheduler),
          map((employees: Employee[]) => LoadedListEmployeesFromLocalDBSuccess({ list: employees })),
          catchError((error: Error) => of(LoadedListEmployeesFromLocalDBFailure({ error })))
        )))
  });

  updateListEmployees$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UpdateListEmployees),
      observeOn(asyncScheduler),
      map(({ data }) => {
        if (data?.['properties']) {

          const list: Employee[] = [data?.['properties']];

          return UpdatedListEmployeesSuccess({ list })
        } else {
          return UpdatedListEmployeesSuccess({ list: [] });
        }
      })
    )
  });

  selectActiveEmployee$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SelectActiveEmployee),
      observeOn(asyncScheduler),
      map(({ employee: { uuid } }) => this.localStorage.set(StorageKeys.activeEmployee, uuid))
    )
  }, { dispatch: false });

  clearActiveEmployee$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClearActiveEmployee),
      observeOn(asyncScheduler),
      map(() => this.localStorage.remove(StorageKeys.activeEmployee))
    )
  }, { dispatch: false });

  sendErrorsToLogs$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LoadedListEmployeesFromLocalDBFailure),
      map((action) => this.logger.error(action.error, action.type))
    )
  }, { dispatch: false })

  // "No active employee" is a valid state only on the employee-select and settings screens.
  private isNeedReSelectEmployee(): boolean {
    const currentUrl: string = window.location.hash;

    if (!currentUrl) {
      return true;
    }

    return !['settings', 'employee'].some((url: string) => currentUrl.includes(url));
  }
}
